/**
 * React hooks for cache invalidation functionality
 * Provides easy access to cache invalidation features in React components
 */

import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  getCacheInvalidationManager, 
  CacheInvalidationEvent, 
  CacheFreshness,
  invalidateTrackingData,
  invalidateOnEmailSent,
  invalidateOnCampaignUpdate
} from '../utils/cacheInvalidation';

/**
 * Hook for manual cache invalidation operations
 */
export const useCacheInvalidation = () => {
  const queryClient = useQueryClient();
  
  const invalidateByEvent = useCallback((event: CacheInvalidationEvent, campaignId?: string) => {
    const manager = getCacheInvalidationManager();
    manager.invalidateByEvent(event, campaignId);
  }, []);

  const invalidateCampaign = useCallback((campaignId: string, cascade: boolean = true) => {
    const manager = getCacheInvalidationManager();
    manager.invalidateCampaign(campaignId, cascade);
  }, []);

  const invalidateByPattern = useCallback((pattern: string) => {
    const manager = getCacheInvalidationManager();
    manager.invalidateByPattern(pattern);
  }, []);

  const clearAllCaches = useCallback(() => {
    const manager = getCacheInvalidationManager();
    manager.clearAllCaches();
  }, []);

  const backgroundRefresh = useCallback(() => {
    const manager = getCacheInvalidationManager();
    manager.backgroundRefreshStaleData();
  }, []);

  const prefetchCampaign = useCallback((campaignId: string) => {
    const manager = getCacheInvalidationManager();
    manager.prefetchCampaignData(campaignId);
  }, []);

  return {
    invalidateByEvent,
    invalidateCampaign,
    invalidateByPattern,
    clearAllCaches,
    backgroundRefresh,
    prefetchCampaign,
    // Convenience methods
    invalidateAll: () => invalidateByEvent('manual_refresh'),
    invalidateOnEmailSent: (campaignId: string) => invalidateByEvent('email_sent', campaignId),
    invalidateOnCampaignUpdate: (campaignId: string) => invalidateByEvent('campaign_updated', campaignId),
  };
};

/**
 * Hook for monitoring cache status and statistics
 */
export const useCacheStatus = () => {
  const [cacheStatus, setCacheStatus] = useState(() => {
    try {
      const manager = getCacheInvalidationManager();
      return manager.getCacheStatus();
    } catch {
      return null;
    }
  });

  const refreshStatus = useCallback(() => {
    try {
      const manager = getCacheInvalidationManager();
      setCacheStatus(manager.getCacheStatus());
    } catch (error) {
      console.error('Failed to get cache status:', error);
      setCacheStatus(null);
    }
  }, []);

  // Auto-refresh status every 30 seconds
  useEffect(() => {
    const interval = setInterval(refreshStatus, 30000);
    return () => clearInterval(interval);
  }, [refreshStatus]);

  return {
    cacheStatus,
    refreshStatus,
    isAvailable: cacheStatus !== null,
  };
};

/**
 * Hook for checking cache freshness of specific data
 */
export const useCacheFreshness = (
  queryKey: readonly unknown[], 
  dataType: 'tracking' | 'campaignStats' | 'userProfile'
) => {
  const [freshness, setFreshness] = useState<CacheFreshness>(CacheFreshness.EXPIRED);

  const checkFreshness = useCallback(() => {
    try {
      const manager = getCacheInvalidationManager();
      const currentFreshness = manager.getCacheFreshness(queryKey, dataType);
      setFreshness(currentFreshness);
    } catch (error) {
      console.error('Failed to check cache freshness:', error);
      setFreshness(CacheFreshness.EXPIRED);
    }
  }, [queryKey, dataType]);

  // Check freshness on mount and when queryKey changes
  useEffect(() => {
    checkFreshness();
  }, [checkFreshness]);

  // Auto-refresh freshness check every minute
  useEffect(() => {
    const interval = setInterval(checkFreshness, 60000);
    return () => clearInterval(interval);
  }, [checkFreshness]);

  return {
    freshness,
    isFresh: freshness === CacheFreshness.FRESH,
    isRecent: freshness === CacheFreshness.RECENT,
    isStale: freshness === CacheFreshness.STALE,
    isExpired: freshness === CacheFreshness.EXPIRED,
    checkFreshness,
  };
};

/**
 * Hook for listening to cache invalidation events
 */
export const useCacheInvalidationListener = (
  event: CacheInvalidationEvent,
  callback: () => void,
  dependencies: React.DependencyList = []
) => {
  useEffect(() => {
    try {
      const manager = getCacheInvalidationManager();
      const unsubscribe = manager.onInvalidation(event, callback);
      return unsubscribe;
    } catch (error) {
      console.error('Failed to register cache invalidation listener:', error);
      return () => {};
    }
  }, [event, ...dependencies]);
};

/**
 * Hook for automatic cache invalidation based on user actions
 */
export const useAutoCacheInvalidation = () => {
  const { invalidateByEvent } = useCacheInvalidation();

  // Listen for common user actions that should trigger cache invalidation
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // User returned to tab, refresh stale data in background
        try {
          const manager = getCacheInvalidationManager();
          manager.backgroundRefreshStaleData();
        } catch (error) {
          console.error('Failed to refresh stale data:', error);
        }
      }
    };

    const handleOnline = () => {
      // User came back online, recover from potential API errors
      invalidateByEvent('api_error_recovery');
    };

    const handleStorageChange = (e: StorageEvent) => {
      // Another tab cleared the cache, we should refresh
      if (e.key && e.key.startsWith('rq-') && e.newValue === null) {
        invalidateByEvent('manual_refresh');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [invalidateByEvent]);

  return {
    // Manual triggers for specific actions
    onEmailSent: (campaignId: string) => invalidateByEvent('email_sent', campaignId),
    onCampaignUpdated: (campaignId: string) => invalidateByEvent('campaign_updated', campaignId),
    onUserProfileChanged: () => invalidateByEvent('user_profile_changed'),
    onManualRefresh: () => invalidateByEvent('manual_refresh'),
  };
};

/**
 * Hook for optimistic updates with automatic rollback
 */
export const useOptimisticUpdate = () => {
  const queryClient = useQueryClient();

  const optimisticUpdate = useCallback(<T>(
    queryKey: readonly unknown[],
    updater: (oldData: T | undefined) => T,
    rollbackOnError: boolean = true
  ) => {
    try {
      const manager = getCacheInvalidationManager();
      manager.optimisticUpdate(queryKey, updater, rollbackOnError);
    } catch (error) {
      console.error('Failed to perform optimistic update:', error);
      // Fallback to direct query client update
      const previousData = queryClient.getQueryData<T>(queryKey);
      queryClient.setQueryData(queryKey, updater);
      
      if (rollbackOnError) {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey })
            .catch(() => {
              queryClient.setQueryData(queryKey, previousData);
            });
        }, 1000);
      }
    }
  }, [queryClient]);

  return { optimisticUpdate };
};

/**
 * Hook for keyboard shortcuts related to cache management
 */
export const useCacheKeyboardShortcuts = () => {
  const { invalidateAll, backgroundRefresh, clearAllCaches } = useCacheInvalidation();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+Shift+R or Cmd+Shift+R - Force refresh all data
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'R') {
        event.preventDefault();
        invalidateAll();
        console.log('Manual cache refresh triggered via keyboard shortcut');
      }
      
      // Ctrl+Shift+B or Cmd+Shift+B - Background refresh stale data
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'B') {
        event.preventDefault();
        backgroundRefresh();
        console.log('Background refresh triggered via keyboard shortcut');
      }
      
      // Ctrl+Shift+Alt+C or Cmd+Shift+Alt+C - Clear all caches (dangerous)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.altKey && event.key === 'C') {
        event.preventDefault();
        if (window.confirm('Are you sure you want to clear all caches? This will remove all cached data.')) {
          clearAllCaches();
          console.log('All caches cleared via keyboard shortcut');
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [invalidateAll, backgroundRefresh, clearAllCaches]);
};